import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { OnChangeFn, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

import {
  type OpportunityInfluencedListDataType,
  type OpportunityStatusOldType,
  useOpportunityInfluencedList,
} from '@/api/opportunity';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import { InstantSearchSortOrder, type QueryState } from '@/components/InstantSearch';
import { type DateRangeType, usePageFilterContext } from '@/components/page';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

const resolveUpdater = <T,>(updater: Updater<T>, previous: T): T =>
  typeof updater === 'function' ? (updater as (old: T) => T)(previous) : updater;

type Props = {
  opportunityType: OpportunityStatusOldType;
  isLoading?: boolean;
  selectedDateRange?: DateRangeType;
  vendorIds?: string[];
  campaignIds?: string[];
  pixelIds?: string[];
  audienceIds?: string[];
  canExecuteQuery?: boolean;
  onTableStateChange?: (tableState: Omit<QueryState, 'search' | 'filters'>) => void;
};

const OpportunityInfluenceTable = ({
  opportunityType,
  isLoading,
  selectedDateRange,
  vendorIds,
  campaignIds,
  pixelIds,
  audienceIds,
  canExecuteQuery,
}: Props) => {
  const {
    pageFilters: { startDate, endDate },
  } = usePageFilterContext();

  const [tableState, setTableState] = useState<Omit<QueryState, 'search' | 'filters'>>({
    page: 1,
    size: 10,
    sortBy: 'date',
    sortOrder: InstantSearchSortOrder.DESC,
  });

  const {
    opportunitiesListData: data,
    totalResults,
    isLoading: opportunitiesListIsLoading,
    error,
  } = useOpportunityInfluencedList(
    tableState,
    opportunityType,
    {
      startDate: selectedDateRange?.startDate ?? startDate,
      endDate: selectedDateRange?.endDate ?? endDate,
      campaignIds,
      vendorIds,
      pixelIds,
      audienceIds,
    },
    canExecuteQuery,
  );

  const columns = useMemo<DataTableColumnDef<OpportunityInfluencedListDataType>[]>(
    () => [
      {
        header: t`Company Name`,
        id: 'account.company.name',
        accessorKey: 'accountCompanyName',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => row.original.accountCompanyName,
      },
      {
        header: t`Opportunity`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => (
          <Link variant="caption1" to={ROUTES.opportunityById(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: opportunityType === 'pipeline' ? t`Create Date` : t`Close Date`,
        id: 'date',
        accessorFn: (row) => (opportunityType === 'pipeline' ? row.openDate : row.closeDate),
        enableSorting: true,
        size: 120,
        cell: ({ row }) =>
          dayjs(
            opportunityType === 'pipeline' ? row.original.openDate : row.original.closeDate,
          ).format(USDateViewFormat),
      },
      {
        header: t`Impressions`,
        id: 'metrics.impressions',
        accessorKey: 'metricsImpressions',
        enableSorting: true,
        size: 120,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.metricsImpressions),
      },
      {
        header: t`Visits`,
        id: 'metrics.visits',
        accessorKey: 'metricsVisits',
        enableSorting: true,
        size: 100,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.metricsVisits),
      },
      {
        header: t`Opportunity Amount`,
        id: 'amount',
        accessorKey: 'amount',
        enableSorting: true,
        size: 120,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.amount, { isCurrency: true, precision: 0 }),
      },
    ],
    [opportunityType],
  );

  const sorting = useMemo<SortingState>(
    () => [
      {
        id: tableState.sortBy,
        desc: tableState.sortOrder === InstantSearchSortOrder.DESC,
      },
    ],
    [tableState.sortBy, tableState.sortOrder],
  );

  const paginationState = useMemo<PaginationState>(
    () => ({
      pageIndex: Math.max(tableState.page - 1, 0),
      pageSize: tableState.size,
    }),
    [tableState.page, tableState.size],
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setTableState((s) => {
      const currentSorting: SortingState = [
        { id: s.sortBy, desc: s.sortOrder === InstantSearchSortOrder.DESC },
      ];
      const nextSorting = resolveUpdater(updater, currentSorting);
      const first = nextSorting[0];
      if (first == null) {
        return s;
      }
      return {
        ...s,
        sortBy: first.id,
        sortOrder: first.desc ? InstantSearchSortOrder.DESC : InstantSearchSortOrder.ASC,
        page: 1,
      };
    });
  }, []);

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>((updater) => {
    setTableState((s) => {
      const current: PaginationState = {
        pageIndex: Math.max(s.page - 1, 0),
        pageSize: s.size,
      };
      const next = resolveUpdater(updater, current);
      return {
        ...s,
        page: next.pageIndex + 1,
        size: next.pageSize,
      };
    });
  }, []);

  return (
    <div style={{ padding: '20px 32px' }}>
      <DataTable<OpportunityInfluencedListDataType>
        columns={columns}
        data={data ?? []}
        loading={isLoading || opportunitiesListIsLoading}
        emptyMessage={
          opportunityType === 'closedWon' ? (
            <Trans>No Closed Won opportunities found.</Trans>
          ) : (
            <Trans>No Pipeline opportunities found.</Trans>
          )
        }
        error={error}
        rowCount={totalResults}
        manualPagination
        pagination={{
          paginationState,
          onPaginationChange: handlePaginationChange,
        }}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        manualSorting
        getRowId={(record) => record.id}
        initialColumnPinning={{
          start: ['account.company.name'],
        }}
      />
    </div>
  );
};

export default OpportunityInfluenceTable;
